import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { Link } from 'react-router-dom';

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

export const Input = styled.input`
  padding: 10px;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  box-sizing: border-box;

  ${(p) =>
    p.$glass
      ? css`
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(255, 255, 255, 0.45);
          color: #0f172a;
          &::placeholder {
            color: #64748b;
          }
          &:focus {
            outline: none;
            border-color: #14464f;
            box-shadow: 0 0 0 1px rgba(20, 70, 79, 0.25);
          }
        `
      : css`
          border: 1px solid #ddd;
          &:focus {
            outline: none;
            border-color: #14464f;
          }
        `}
`;

export const Button = styled.button`
  padding: 12px;
  background: #14464F;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;

  &:hover {
    background: #254F51;
  }
`;

const PasswordContainer = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled(Input)`
  padding-right: 40px; // Make room for the eye icon
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.$glass ? '#475569' : '#666')};

  &:hover {
    color: ${(p) => (p.$glass ? '#14464f' : '#14464F')};
  }

  &:focus {
    outline: none;
  }
`;

const SUPPORT_EMAIL = 'contact@quisqueyaquest.com';

const ForgotPasswordRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin: -6px 0 2px;
`;

const ForgotPasswordLink = styled.a`
  color: ${(p) => (p.$glass ? '#b8efe8' : '#14464F')};
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${(p) => (p.$glass ? '#e6fffb' : '#0d3a42')};
  }
`;

export const PasswordField = ({ value, onChange, placeholder, glass }) => {
  const [showPassword, setShowPassword] = useState(false);
  const { language } = useLanguage();
  const t = translations[language].auth;

  return (
    <PasswordContainer>
      <PasswordInput
        $glass={glass}
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder || t.password}
        required
      />
      <PasswordToggle
        $glass={glass}
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        title={showPassword ? 'Hide password' : 'Show password'}
      >
        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
      </PasswordToggle>
    </PasswordContainer>
  );
};

export const LoginForm = ({ onSubmit, isLoading, glass }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { language } = useLanguage();
  const t = translations[language].auth;

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit({ email, password });
  };

  const forgotPasswordHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    t.forgotPasswordMailSubject
  )}&body=${encodeURIComponent(t.forgotPasswordMailBody)}`;

  return (
    <Form onSubmit={handleSubmit}>
      <Input
        $glass={glass}
        type="email"
        placeholder={t.email}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        disabled={isLoading}
      />
      <PasswordField
        glass={glass}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <ForgotPasswordRow>
        <ForgotPasswordLink $glass={glass} href={forgotPasswordHref}>
          {t.forgotPassword}
        </ForgotPasswordLink>
      </ForgotPasswordRow>
      <Button type="submit" disabled={isLoading} style={{ opacity: isLoading ? 0.7 : 1 }}>
        {isLoading ? (language === 'en' ? 'Signing in...' : 'Iniciando sesión...') : t.signIn}
      </Button>
    </Form>
  );
};

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  margin-top: 4px;
`;

const CheckboxLabel = styled.label`
  color: ${(p) => (p.$glass ? 'rgba(255, 255, 255, 0.9)' : '#666')};
  font-size: 14px;
  line-height: 1.4;
`;

const TermsLink = styled(Link)`
  color: ${(p) => (p.$glass ? '#b8efe8' : '#14464F')};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export const SignupForm = ({ onSubmit, glass }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { language } = useLanguage();
  const t = translations[language].auth;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      // You might want to show an error message here
      return;
    }
    onSubmit({ firstName, lastName, email, password });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Input
        $glass={glass}
        type="text"
        placeholder={t.firstName}
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
      />
      <Input
        $glass={glass}
        type="text"
        placeholder={t.lastName}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        required
      />
      <Input
        $glass={glass}
        type="email"
        placeholder={t.email}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <PasswordField
        glass={glass}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          id="terms"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          required
        />
        <CheckboxLabel $glass={glass} htmlFor="terms">
          {language === 'en' ? 'I accept the ' : 'Acepto los '}
          <TermsLink $glass={glass} to="/legal/terms" target="_blank">
            {language === 'en' ? 'Terms and Conditions' : 'Términos y Condiciones'}
          </TermsLink>
        </CheckboxLabel>
      </CheckboxContainer>
      <Button type="submit">{t.signUp}</Button>
    </Form>
  );
}; 